import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, MessageCircle, Bug, Lightbulb, Shield, MapPin, Building } from "lucide-react";

const Contact = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Kontakt</h1>
            <p className="text-xl text-gray-600">Wir sind hier, um Ihnen zu helfen.</p>
          </div>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-uplivion-blue/5 to-uplivion-blue/10 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Building className="w-8 h-8 text-uplivion-blue flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold text-uplivion-blue mb-3">Arc Rider Ventures GmbH</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold mb-1">Anschrift</h3>
                        <p className="text-sm text-gray-600">
                          Jänickendorfer Str. 64a<br/>
                          14943 Brandenburg<br/>
                          Deutschland
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Rechtliche Informationen</h3>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Handelsregister:</strong> Amtsgericht Berlin Charlottenburg HRB199081B
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Geschäftsführer:</strong> Jakob Jordan
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h2 className="text-2xl font-semibold mb-6">Schnelle Hilfe</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                  <MessageCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Allgemeine Fragen</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Fragen zur Nutzung, Features oder Ihrem Konto
                    </p>
                    <a 
                      href="mailto:support@uplivion.life" 
                      className="text-blue-600 hover:underline text-sm font-medium"
                    >
                      support@uplivion.life
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                  <Bug className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Technischer Support</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Probleme, Bugs oder technische Schwierigkeiten
                    </p>
                    <a 
                      href="mailto:support@uplivion.life" 
                      className="text-green-600 hover:underline text-sm font-medium"
                    >
                      support@uplivion.life
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                  <Lightbulb className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Feedback & Ideen</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Verbesserungsvorschläge und neue Feature-Ideen
                    </p>
                    <a 
                      href="mailto:support@uplivion.life" 
                      className="text-purple-600 hover:underline text-sm font-medium"
                    >
                      support@uplivion.life
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <Shield className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Datenschutz & Rechtliches</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Fragen zu Datenschutz, AGB oder rechtlichen Angelegenheiten
                    </p>
                    <a 
                      href="mailto:support@uplivion.life" 
                      className="text-orange-600 hover:underline text-sm font-medium"
                    >
                      support@uplivion.life
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-6">Beta-Support</h2>
              <div className="bg-gradient-to-br from-uplivion-blue/5 to-uplivion-blue/10 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-uplivion-blue mb-3">Persönlicher Beta-Support</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Als Beta-Nutzer erhalten Sie direkten Support von unserem Entwicklerteam. 
                  Wir antworten in der Regel innerhalb von 24 Stunden.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Schnelle Hilfe bei Problemen</li>
                  <li>• Direkter Draht zu den Entwicklern</li>
                  <li>• Ihre Ideen werden gehört</li>
                  <li>• Regelmäßige Updates über neue Features</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-yellow-800 mb-2">Bevor Sie uns schreiben</h4>
                <p className="text-sm text-yellow-700 mb-3">
                  Schauen Sie kurz in unsere FAQ - vielleicht finden Sie dort bereits eine Antwort:
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/faq">FAQ durchsuchen</Link>
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold mb-3">Hilfreiche Informationen</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Um Ihnen schneller helfen zu können, fügen Sie bitte folgende Informationen hinzu:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Browser und Version (z.B. Chrome 91, Safari 14)</li>
                  <li>• Betriebssystem (Windows, Mac, iOS, Android)</li>
                  <li>• Beschreibung des Problems</li>
                  <li>• Screenshots bei visuellen Problemen</li>
                  <li>• Schritte zur Reproduktion des Problems</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t pt-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-4">Weitere Hilfe</h2>
              <p className="text-gray-600 mb-6">
                Nutzen Sie unsere weiteren Ressourcen für Antworten und Hilfe
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Button variant="outline" asChild className="h-auto p-4">
                <Link to="/help" className="flex flex-col items-center space-y-2">
                  <Mail className="w-6 h-6" />
                  <span>Hilfe-Center</span>
                </Link>
              </Button>
              <Button variant="outline" asChild className="h-auto p-4">
                <Link to="/faq" className="flex flex-col items-center space-y-2">
                  <MessageCircle className="w-6 h-6" />
                  <span>FAQ</span>
                </Link>
              </Button>
              <Button variant="outline" asChild className="h-auto p-4">
                <Link to="/about" className="flex flex-col items-center space-y-2">
                  <Lightbulb className="w-6 h-6" />
                  <span>Über uplivion</span>
                </Link>
              </Button>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
